import type { Json } from "@/integrations/supabase/types";

export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function asJson(value: unknown): Json {
  return value as Json;
}

export function firstJoined<T>(value: T | T[] | null | undefined): T | null {
  if (value == null) return null;
  return Array.isArray(value) ? (value[0] ?? null) : value;
}

export type ChecklistJsonItem = {
  id?: string;
  name?: string;
  task_name?: string;
  completed?: boolean;
  is_completed?: boolean;
  status?: string;
  section_id?: string | null;
  location_id?: string | null;
  photo_required?: boolean;
  requires_photo?: boolean;
  photo_urls?: string[];
  note?: string;
  task_type?: string;
  type?: string;
  metadata?: Record<string, unknown>;
  is_extra_task?: boolean | string;
  step_id?: string;
};

function toChecklistItem(value: Record<string, unknown>, fallbackId?: string): ChecklistJsonItem {
  const photoUrls = Array.isArray(value.photo_urls)
    ? value.photo_urls.filter((url): url is string => typeof url === "string")
    : undefined;

  return {
    id: typeof value.id === "string" ? value.id : fallbackId,
    name: typeof value.name === "string" ? value.name : undefined,
    task_name: typeof value.task_name === "string" ? value.task_name : undefined,
    completed: value.completed === true,
    is_completed: value.is_completed === true,
    status: typeof value.status === "string" ? value.status : undefined,
    section_id: typeof value.section_id === "string" ? value.section_id : null,
    location_id: typeof value.location_id === "string" ? value.location_id : null,
    photo_required: value.photo_required === true,
    requires_photo: value.requires_photo === true,
    photo_urls: photoUrls,
    note: value.note != null ? String(value.note) : undefined,
    task_type: typeof value.task_type === "string" ? value.task_type : undefined,
    type: typeof value.type === "string" ? value.type : undefined,
    metadata: isPlainObject(value.metadata) ? value.metadata : undefined,
    is_extra_task:
      typeof value.is_extra_task === "boolean" || typeof value.is_extra_task === "string"
        ? value.is_extra_task
        : undefined,
    step_id: typeof value.step_id === "string" ? value.step_id : undefined,
  };
}

export function parseChecklistItems(checklist: unknown): ChecklistJsonItem[] {
  if (!checklist) return [];
  if (Array.isArray(checklist)) {
    return checklist.filter(isPlainObject).map((item) => toChecklistItem(item));
  }
  if (isPlainObject(checklist) && Array.isArray(checklist.items)) {
    return checklist.items.filter(isPlainObject).map((item) => toChecklistItem(item));
  }
  if (isPlainObject(checklist)) {
    return Object.entries(checklist).map(([key, value]) => {
      const item = isPlainObject(value) ? value : {};
      return toChecklistItem(item, key);
    });
  }
  return [];
}

export function checklistDisplayName(item: ChecklistJsonItem, fallback = ""): string {
  return item.name || item.task_name || item.id || fallback;
}

export function checklistRequiresPhoto(item: ChecklistJsonItem, checklist: unknown): boolean {
  if (item.photo_required || item.requires_photo) return true;
  return isPlainObject(checklist) && (checklist.requires_photo === true || checklist.photo_required === true);
}

export function checklistPhotoUrls(item: ChecklistJsonItem, checklist: unknown): string[] {
  if (item.photo_urls && item.photo_urls.length > 0) return item.photo_urls;
  if (isPlainObject(checklist) && Array.isArray(checklist.photo_urls)) {
    return checklist.photo_urls.filter((url): url is string => typeof url === "string");
  }
  return [];
}
